import type {
  AnalysisSpec,
  AnalyticsBootstrap,
  AnalyticsContentScope,
  AnalyticsUserPreference,
  AskRequest,
  ChartTemplate,
  DashboardDatePreset,
  DashboardDefinition,
  EvidenceFact,
  SemanticFilter,
  SemanticQueryTrace,
  VisualAnswer,
  VisualDefinition,
  VisualSpec,
  VisualExecutionMeta,
  AnalyticsInsightJob,
  AnalyticsInsightRevision,
  AnalyticsInsightThread,
  AnalyticsDashboardSnapshot,
  AnalyticsDashboardSnapshotItem,
  AnalyticsCompanySettings,
  ChartThemeDefinition,
  ChartThemePayload,
  ChartThemeSummary,
  PortableChartTheme,
  ThemeAccessibilityCheck,
  ThemeGenerationResult,
} from '@lognai/plugin-analytics';
import { apiFetch, apiPost } from '../api/client';

export interface AnalysisResult {
  analysis: AnalysisSpec;
  visual: VisualSpec;
  evidence: EvidenceFact[];
  trace: SemanticQueryTrace[];
  meta?: VisualExecutionMeta;
}

export interface VisualPage {
  items: VisualDefinition[];
  total: number;
  page: number;
  pageSize: number;
  facets: { domains: Record<string, number>; templates: Record<string, number>; scopes: Record<string, number> };
  usage: Record<string, { score: number; lastUsedAt?: string }>;
}

export interface BatchResult {
  visualId: string;
  ok: boolean;
  result?: AnalysisResult;
  error?: string;
}

export const getAnalyticsBootstrap = (): Promise<AnalyticsBootstrap> => apiFetch('/api/analytics/bootstrap');

export const listChartThemes = (includeArchived = false): Promise<{ items: ChartThemeSummary[] }> =>
  apiFetch(`/api/analytics/themes${includeArchived ? '?includeArchived=true' : ''}`);

export const getChartTheme = (id: string): Promise<{ theme: ChartThemeDefinition; portable: PortableChartTheme; checks: ThemeAccessibilityCheck[] }> =>
  apiFetch(`/api/analytics/themes/${encodeURIComponent(id)}`);

export const createChartTheme = (input: { name: string; description: string; draft: ChartThemePayload } | { portable: PortableChartTheme }): Promise<ChartThemeDefinition> =>
  apiPost('/api/analytics/themes', input);

export const updateChartTheme = (id: string, expectedVersion: number, patch: { name?: string; description?: string; draft?: ChartThemePayload }): Promise<ChartThemeDefinition> =>
  apiFetch(`/api/analytics/themes/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedVersion, patch }),
  });

export const cloneChartTheme = (id: string): Promise<ChartThemeDefinition> =>
  apiPost(`/api/analytics/themes/${encodeURIComponent(id)}/clone`, {});

export const publishChartTheme = (id: string, expectedVersion: number, confirmWarnings = false): Promise<ChartThemeDefinition> =>
  apiPost(`/api/analytics/themes/${encodeURIComponent(id)}/publish`, { expectedVersion, confirmWarnings });

export const setDefaultChartTheme = (id: string, expectedVersion: number): Promise<ChartThemeDefinition> =>
  apiPost(`/api/analytics/themes/${encodeURIComponent(id)}/set-default`, { expectedVersion });

export const archiveChartTheme = (id: string, expectedVersion: number): Promise<{ archived: true }> =>
  apiFetch(`/api/analytics/themes/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`, { method: 'DELETE' });

export const validateChartTheme = (draft: ChartThemePayload): Promise<{ valid: true; checks: ThemeAccessibilityCheck[] }> =>
  apiPost('/api/analytics/themes/validate', { draft });

export const generateChartTheme = (prompt: string, currentDraft?: ChartThemePayload): Promise<ThemeGenerationResult> =>
  apiPost('/api/analytics/themes/generate', { prompt, currentDraft });

export interface ResolvedChartThemeResponse {
  id: string;
  name: string;
  source: 'dashboard' | 'company' | 'system';
  payload: ChartThemePayload;
  requestedThemeUnavailable: boolean;
}

export const resolveChartTheme = (dashboardId?: string): Promise<ResolvedChartThemeResponse> =>
  apiFetch(`/api/analytics/themes/resolve${dashboardId ? `?dashboardId=${encodeURIComponent(dashboardId)}` : ''}`);

export const listVisuals = (params: {
  q?: string; domains?: string[]; templates?: ChartTemplate[]; scopes?: AnalyticsContentScope[];
  favorites?: boolean; sort?: 'relevance' | 'popular' | 'recent' | 'title'; page?: number; pageSize?: number;
} = {}): Promise<VisualPage> => {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.domains?.length) query.set('domains', params.domains.join(','));
  if (params.templates?.length) query.set('templates', params.templates.join(','));
  if (params.scopes?.length) query.set('scopes', params.scopes.join(','));
  if (params.favorites) query.set('favorites', 'true');
  if (params.sort) query.set('sort', params.sort);
  query.set('page', String(params.page ?? 1));
  query.set('pageSize', String(params.pageSize ?? 24));
  return apiFetch(`/api/analytics/visuals?${query}`);
};

export const getVisual = (id: string): Promise<VisualDefinition> => apiFetch(`/api/analytics/visuals/${encodeURIComponent(id)}`);
export const cloneVisual = (id: string): Promise<VisualDefinition> => apiPost(`/api/analytics/visuals/${encodeURIComponent(id)}/clone`, {});

export const executeAnalysis = (analysis: AnalysisSpec): Promise<AnalysisResult> =>
  apiPost('/api/analytics/visuals/preview', { analysis });

export const executeVisualBatch = (items: Array<{ visualId: string; filters?: SemanticFilter[]; dateRange?: [string, string] }>): Promise<{ results: BatchResult[] }> =>
  apiPost('/api/analytics/visuals/execute-batch', { items });

export const askAnalytics = (request: AskRequest): Promise<VisualAnswer> => apiPost('/api/analytics/ask', request);

export const createVisual = (input: {
  title: string; description: string; businessQuestion: string; scope: 'private' | 'company'; origin: 'manual' | 'ai';
  analysis: AnalysisSpec; presentation: { template: ChartTemplate }; domains: string[]; workflows: string[]; tags: string[];
}): Promise<VisualDefinition> => apiPost('/api/analytics/visuals', input);

export const listDashboards = (options: { includeArchived?: boolean; deduplicate?: boolean } = {}): Promise<DashboardDefinition[]> => {
  const query = new URLSearchParams();
  if (options.includeArchived) query.set('includeArchived', 'true');
  if (options.deduplicate === false) query.set('deduplicate', 'false');
  return apiFetch(`/api/analytics/dashboards${query.size ? `?${query}` : ''}`);
};
export const getDashboard = (id: string): Promise<DashboardDefinition> => apiFetch(`/api/analytics/dashboards/${encodeURIComponent(id)}`);
export const createDashboard = (input: {
  key: string;
  name: string;
  description: string;
  scope: 'private' | 'company';
  widgets: DashboardDefinition['widgets'];
  layouts: DashboardDefinition['layouts'];
  filters: DashboardDefinition['filters'];
  domains: string[];
  workflows: string[];
  tags: string[];
  isCompanyDefault?: boolean;
  themeId?: string | null;
  insights?: DashboardDefinition['insights'];
  history?: DashboardDefinition['history'];
}): Promise<DashboardDefinition> => apiPost('/api/analytics/dashboards', input);
export const cloneDashboard = (id: string): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/clone`, {});
export const makeDashboardEditable = (id: string): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/editable`, {});
export const resetDashboard = (id: string, expectedVersion: number): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/reset`, { expectedVersion });
export const revertDashboard = (id: string, expectedVersion: number): Promise<DashboardDefinition> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/revert`, { expectedVersion });

export interface DashboardBriefingResult {
  dashboardId: string;
  datePreset: DashboardDatePreset;
  kpis: Array<{
    definition: NonNullable<DashboardDefinition['kpis']>[number];
    value: number;
    previousValue?: number;
    deltaPct?: number;
    unit: 'currency' | 'percent' | 'count' | 'days' | 'number';
    freshnessAt: string;
    trace: SemanticQueryTrace;
  }>;
  insight: VisualAnswer['insight'];
  evidence: EvidenceFact[];
  freshnessAt: string;
}

export const getDashboardBriefing = (id: string, datePreset: DashboardDatePreset, includeAi = false): Promise<DashboardBriefingResult> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(id)}/briefing`, { datePreset, includeAi });

export const recordVisualUsage = (id: string, action: 'previewed' | 'added_to_dashboard' | 'created'): Promise<{ recorded: true }> =>
  apiPost(`/api/analytics/visuals/${encodeURIComponent(id)}/usage`, { action });

export const updateDashboard = (id: string, expectedVersion: number, patch: Omit<Partial<DashboardDefinition>, 'themeId'> & { themeId?: string | null }): Promise<DashboardDefinition> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedVersion, patch }),
  });

export const archiveDashboard = (id: string, expectedVersion: number): Promise<DashboardDefinition> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`, { method: 'DELETE' });

export const updatePreferences = (patch: Partial<AnalyticsUserPreference>): Promise<AnalyticsUserPreference> =>
  apiFetch('/api/analytics/preferences', {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
  });

export interface ActiveInsight {
  thread: AnalyticsInsightThread;
  revision: AnalyticsInsightRevision;
  stale: boolean;
}

export const listDashboardInsights = (dashboardId: string, datePreset?: DashboardDatePreset): Promise<ActiveInsight[]> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights${datePreset ? `?datePreset=${encodeURIComponent(datePreset)}` : ''}`);

export const saveManualInsight = (dashboardId: string, input: {
  targetType: 'dashboard' | 'widget'; widgetId?: string; headline: string; summary: string; expectedThreadVersion?: number; editThreadId?: string;
}): Promise<ActiveInsight> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights/manual`, input);

export const createPersistedInsightJob = (dashboardId: string, input: {
  targetType: 'dashboard' | 'widget'; widgetId?: string; question: string; analysis: AnalysisSpec;
  selection?: { member: string; key: string; label?: string };
  context: { datePreset: DashboardDatePreset; filters: SemanticFilter[]; drillState: Array<{ member: string; key: string }>; visualVersion?: number };
}): Promise<AnalyticsInsightJob> => apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insight-jobs`, input);

export const getPersistedInsightJob = (dashboardId: string, jobId: string): Promise<AnalyticsInsightJob> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insight-jobs/${encodeURIComponent(jobId)}`);

export const getInsightRevisions = (dashboardId: string, threadId: string): Promise<AnalyticsInsightRevision[]> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights/${encodeURIComponent(threadId)}/revisions`);

export const archiveInsight = (dashboardId: string, threadId: string, expectedVersion: number): Promise<AnalyticsInsightThread> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/insights/${encodeURIComponent(threadId)}?expectedVersion=${expectedVersion}`, { method: 'DELETE' });

export const listDashboardSnapshots = (dashboardId: string): Promise<AnalyticsDashboardSnapshot[]> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots`);

export const createDashboardSnapshot = (dashboardId: string, input: { label?: string; datePreset?: DashboardDatePreset }): Promise<AnalyticsDashboardSnapshot> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots`, input);

export const getDashboardSnapshot = (dashboardId: string, snapshotId: string): Promise<{ snapshot: AnalyticsDashboardSnapshot; items: AnalyticsDashboardSnapshotItem[] }> =>
  apiFetch(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}`);

export interface DashboardComparisonResult {
  dashboardId: string;
  snapshot: AnalyticsDashboardSnapshot;
  historicalItems: AnalyticsDashboardSnapshotItem[];
  current: { capturedAt: string; datePreset: DashboardDatePreset };
  kpis: Array<{
    id: string; label: string; unit: 'currency' | 'percent' | 'count' | 'days' | 'number';
    current?: number; historical?: number; classification: string;
    delta?: { absolute: number; percentage?: number };
  }>;
  widgets: Array<{
    widgetId: string; visualId: string; classification: string;
    current?: { ok: boolean; result?: { execution: { visual: VisualSpec; analysis: AnalysisSpec; evidence: EvidenceFact[]; trace: SemanticQueryTrace }; meta: VisualExecutionMeta } };
    historical?: AnalyticsDashboardSnapshotItem;
  }>;
}

export const compareDashboardSnapshot = (dashboardId: string, snapshotId: string): Promise<DashboardComparisonResult> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}/compare`, {});

export const createComparisonInsightJob = (dashboardId: string, snapshotId: string, question = 'What changed since this dashboard snapshot?'): Promise<AnalyticsInsightJob> =>
  apiPost(`/api/analytics/dashboards/${encodeURIComponent(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}/insight-jobs`, { question });

export const getAnalyticsHistorySettings = (): Promise<AnalyticsCompanySettings> => apiFetch('/api/analytics/history-settings');

export const updateAnalyticsHistorySettings = (input: {
  expectedVersion: number; historyRetentionDays?: number; captureLocalHour?: number; timezone?: string;
}): Promise<AnalyticsCompanySettings> => apiFetch('/api/analytics/history-settings', {
  method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
});
